import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { emojisplosion } from "emojisplosion";
import { isSameDay, parseISO, format } from "date-fns";
import { toast } from "react-toastify";
import { green } from "@material-ui/core/colors";
import clsx from "clsx";

import { Videoplayer } from "@openabos/react-circular-player";
import "@openabos/react-circular-player/dist/cjs/index.css";
import "../../styles/react-circular-player-new-styles.css";
import "../../styles/react-three-dots-elastic.css";

import {
  Avatar,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Tooltip,
  useMediaQuery
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  ExpandMoreOutlined,
  FilterTiltShiftOutlined,
  FormatListBulletedOutlined,
  GetApp,
  MissedVideoCallRounded,
  NotInterestedOutlined,
  PhoneMissedRounded,
  RecordVoiceOverRounded,
  ReplyOutlined,
  SentimentSatisfiedAltOutlined,
  ShareOutlined,
  StarOutlined,
  VisibilityOutlined
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import api from "../../services/api";
import extractDayMonthYearFromStringDateTime from "../../utils/datetime/extractDayMonthYearFromStringDateTime";
import getParentNodeByClassName from "../../utils/getParentNodeByClassName";
import toastError from "../../errors/toastError";

import AdsMessage from "../AdsMessage";
import Audio from "../Audio";
import ButtonPreview from "../ButtonPreview";
import ConfirmationModal from "../ConfirmationModal";
import EventPreview from "../EventPreview";
import EventPreviewQuotedMessage from "../EventPreviewQuotedMessage";
import GroupInvitePreview from "../GroupInvitePreview";
import ListPreview from "../ListPreview";
import LocationPreview from "../LocationPreview";
import MediaWithNoUrl from "../MediaWithNoUrl";
import MessageBodyWrapper from "../MessageBodyWrapper";
import MessageEditModal from "../MessageEditModal";
import MessageEditHistoryModal from "../MessageEditHistoryModal";
import MessageImage from "../MessageImage";
import MessageNotAvailable from "../MessageNotAvailable";
import MessagePropertiesModal from "../MessagePropertiesModal";
import MessageOptionsMenu from "../MessageOptionsMenu";
import MessageUtils from "../../utils/classes/MessageUtils";
import MultiVcardPreview from "../MultiVcardPreview";
import PhoneNumberMessageOptionsMenu from "../PhoneNumberMessageOptionsMenu";
import PixMessage from "../PixMessage";
import PollPreview from "../PollPreview";
import PollPreviewQuotedMessage from "../PollPreviewQuotedMessage";
import PollVotesModal from "../PollVotesModal";
import VcardPreview from "../VcardPreview";
import ViewOnceMessage from "../ViewOnceMessage";
import ViewOnceMessageWeb from "../ViewOnceMessageWeb";
import ViewOnceRepliedMessage from "../ViewOnceRepliedMessage";

import disableDragEvent from "../../utils/disableDragEvent";
import saveFile from "../../utils/saveFile";
import useWhatsApps from "../../hooks/useWhatsApps";

import CryptoUtils from "../../utils/classes/CryptoUtils";
import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import TemplateMessageUtils from "../../utils/classes/TemplateMessageUtils";

import SocketMessagesList from "../../context/Socket/Listeners/SocketMessagesList";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles((theme) => ({
  quotedMessageAnchor: { textDecoration: "none", color: theme.palette.text.primary, },

  scrollBottomContainer: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    position: "absolute",
    right: "1em",
    bottom: "1em",
    zIndex: "9",
    opacity: "0.80",
    cursor: "pointer",
    padding: "5px",
    borderRadius: "7px",
    transition: "all 0.3s ease",
    "&:hover": { opacity: "1.00", },
  },

  scrollBottomButton: {
    color: theme.palette.text.primary,
    padding: "0px",
  },

  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
  },

  messageNotSentAlertContainer: {
    height: "100%",
    paddingTop: "1em",
    paddingLeft: "1em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  messageNotSentAlertButton: { fontSize: "1.75em", cursor: "pointer", "&:hover": { opacity: "0.8", }, },

  contactAvatarContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  contactAvatar: {
    width: "30px",
    height: "30px",
    marginRight: "10px",
    marginLeft: "10px",
    cursor: "pointer",
    transition: 'transform 0.3s',
    '&:hover': { transform: 'translateY(-5px)' },
  },

  callWrapper: { display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", },
  missedCall: { color: "red", fontSize: "18px", },
  horizontalFlip: { rotate: "y 180deg", fontSize: "20px", },
  forwardedMessage: { fontSize: "12px", color: "#998", display: "flex", alignItems: "center", gap: "5px", },
  buttonListFooter: { fontSize: "12px", color: theme.palette.text.primary, },
  messagesListWrapper: { padding: "5px 0 5px 0", overflow: "hidden", position: "relative", display: "flex", flexDirection: "column", flexGrow: 1, },

  messagesList: {
    borderRadius: "20px",
    backgroundImage: theme.backgroundImage,
    display: "flex",
    flexDirection: "column",

    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,

    [theme.breakpoints.down("sm")]: { paddingBottom: "90px", },
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  infoMessageContainer: { display: "flex", flexDirection: "column", gap: "10px", position: "relative", },

  reactionButton: {
    opacity: 0.65,
    display: "block",
    visibility: "hidden",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: { visibility: "visible" },
    "&:hover": { opacity: 1 },
  },

  reactionOptions: {
    display: "none",
    position: "absolute",
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "center",

    gap: "5px",
    borderRadius: "15px",
    backgroundColor: theme.palette.background.paper,
    padding: "7px",
    boxShadow: "0 1px 1px #b3b3b3",
    width: "300px",
    zIndex: 9,

    [theme.breakpoints.down("sm")]: { gap: "2px", padding: "2px", width: "250px", },
  },

  // Arrow
  '@keyframes moveUpDown': { "0%": { rotate: "z 3deg" }, "100%": { rotate: "z -3deg" }, },

  reactionOption: {
    cursor: "pointer",
    fontSize: "20px",
    transition: 'all 0.3s',
    "&:hover": { transform: 'translateY(-5px)', animation: '$moveUpDown 0.3s infinite alternate', },
    [theme.breakpoints.down("sm")]: { fontSize: "18px" },
  },

  reactionOptionOpacity: { position: "relative", bottom: "-5px", opacity: "0.8", },

  reactionList: {
    position: "absolute",
    bottom: "-18px",
    display: "flex",
    flexDirection: "row",
    zIndex: "9",
    gap: "2px",
  },

  reactionListSticker: {
    position: "absolute",
    bottom: "0px",
    left: "5px",
    display: "flex",
    flexDirection: "row",
    zIndex: "9",
    gap: "2px",
  },

  reactionSpan: {
    borderRadius: "20px",
    padding: "3px 3px 3px 3px",
    backgroundColor: theme.palette.background.paper,
    fontSize: "15px",
    userSelect: "none",
    "&:hover": { filter: "brightness(0.92)" },
  },

  containerMessage: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: 15,
    "&:hover #reactionButton": { visibility: "visible" },
    "&:hover #speechButton": { visibility: "visible" },
    "&:hover #shareButton": { visibility: "visible" },
    "&:target": { animation: '$targetMessage 2s linear', borderRadius: "5px" },
  },

  '@keyframes targetMessage': {
    "0%": { backgroundColor: "transparent", },
    "50%": { backgroundColor: theme.palette.primary.rgbaPastel, },
    "100%": { backgroundColor: "transparent", },
  },

  containerMessageLeft: { justifyContent: "left", marginLeft: "-15px", },
  containerMessageRight: { justifyContent: "right", marginRight: "-15px", },

  messageLeftSticker: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "auto",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      visibility: "visible",
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "auto",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      visibility: "visible",
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.background.quotedPaper,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: { flex: "none", width: "4px", backgroundColor: "#6bcbef", },

  messageRightSticker: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "auto",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      visibility: "visible",
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "auto",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      visibility: "visible",
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.primary.messageBox,
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  internalMessage: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "auto",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      visibility: "visible",
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.internalMessage,
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.primary.quotedMessageBox,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: { padding: 10, maxWidth: 300, height: "auto", whiteSpace: "pre-wrap", },
  quotedSideColorRight: { flex: "none", width: "4px", backgroundColor: "#35cd96", },

  messageActionsButton: {
    display: "flex",
    visibility: "hidden",
    position: "absolute",
    top: 0, right: 0,
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: { display: "flex", color: "#6bcbef", fontWeight: 500, },
  textContentItem: { overflowWrap: "break-word", overflowX: "auto", },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    transition: "all 0.30s",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  messageMediaFullScreen: {
    transition: "all 0.30s",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  deletedMediaMessage: { opacity: 0.7, filter: "grayscale(80%)", },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.text.primary,
    userSelect: "none",
    
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "2px",
  },

  editedMessage: {
    fontStyle: "italic",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" }
  },

  timestampStickerLeft: {
    borderRadius: "10px",
    padding: "5px",
    backgroundColor: theme.palette.background.paper,

    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    gap: "2px",
  },

  timestampStickerRight: {
    borderRadius: "10px",
    padding: "5px",
    backgroundColor: theme.palette.primary.messageBox,

    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    gap: "3px",
  },

  timestampStarred: { display: "flex", alignItems: "center", gap: "5px", },
  timeStampStar: { fontSize: 12, },

  notificationSpan: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "75%",
    backgroundColor: theme.palette.background.paper,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.background.paper,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: { color: "#808888", padding: 8, alignSelf: "center", marginLeft: "0px", },
  ackIcons: { fontSize: 18, verticalAlign: "middle", marginLeft: 4, },
  deletedIcon: { fontSize: 18, verticalAlign: "middle", marginRight: 4, },
  ackDoneAllIcon: { color: theme.palette.secondary.ackCheck, fontSize: 18, verticalAlign: "middle", marginLeft: 4, },

  downloadMedia: {
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s",
    gap: "1em",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  lastMessageTicketContainer: {
    display: "flex",
    flexDiretion: "row",
    alignItems: "center",
    justifyContent: "space-between",
    userSelect: "none",
  },

  lastMessageTicketDivider: { width: "100%", height: "1px", },
  lastMessageTicketSpan: { width: "85%", textAlign: "center", },

  stickerMessage: {
    maxHeight: 225,
    maxWidth: 225,
    borderRadius: 5,
    transition: "all 0.3s",
    [theme.breakpoints.down("sm")]: { maxHeight: 150, maxWidth: 150, },
  },

  deletedStickerMessage: { opacity: 0.7, filter: "grayscale(80%)", },

  floatingButton: {
    transition: 'transform 0.3s',
    '&:hover': { transform: 'translateY(-5px)', },
  },

  //  *****************
  //  ** Dot Elastic **
  //  *****************
  dotElasticContainer: {
    backgroundColor: theme.palette.background.paper,
    width: "50px",
    
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: 8,
    boxShadow: "0 1px 1px #b3b3b3",
    
    
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MessagesList = ({
  ticketId, ticketType, ticketStatus,
  isGroup, isForwarding, updateIsForwarding, selectedForwardMessages,
  updateSelectedForwardMessages, setAudioPlayer, setCurrentPlayingAudioTime,
  setLastMessage, whatsappId, isContactBlocked, isContactInactive,
  botAnswerDelay, setTabOpen, messagesList, dispatch,
  shouldRefetchMessagesList, setShouldRefetchMessagesList, setAreMessagesBeingFetched
}) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { user: { profile: userProfile, id: userId, whatsapp: userWhatsapp } } = useContext(AuthContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);

  const reactionOptions = ["👍", "❤️", "😂", "😯", "😥", "😡", "🙏", "🎉"];

  const isFetchingMessagesRef = useRef(false);
  const messagesAbortControllerRef = useRef(null);
  const messagesRequestIdRef = useRef(0);

  const currentTicketId = useRef(ticketId);
  const currentWhatsappId = useRef(whatsappId);
  const lastMessageRef = useRef();
  const currentAudioRef = useRef();
  const videoRefs = useRef({});

  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  const [pageNumber, setPageNumber] = useState(1);
  const [filesExistenceDictionary, setFilesExistenceDictionary] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState({});
  const [selectedPhoneNumberMessage, setSelectedPhoneNumberMessage] = useState(null);

  const [messageEditModalOpen, setMessageEditModalOpen] = useState(false);
  const [messageEditHistoryModalOpen, setMessageEditHistoryModalOpen] = useState(false);
  const [messagePropertiesModalOpen, setMessagePropertiesModalOpen] = useState(false);
  const [selectedMessageEditHistory, setSelectedMessageEditHistory] = useState(null);
  const [selectedMessageProperties, setSelectedMessageProperties] = useState(null);

  const [pollVotesModalOpen, setPollVotesModalOpen] = useState(false);
  const [selectedPollVotesMessage, setSelectedPollVotesMessage] = useState([]);

  const [openPrivateChatConversationModal, setOpenPrivateChatConversationModal] = useState(false);
  const [selectedContactPrivateChat, setSelectedContactPrivateChat] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [phoneNumberMessageOptionsMenuAnchorEl, setPhoneNumberMessageOptionsMenuAnchorEl] = useState(null);

  const messageOptionsMenuOpen = Boolean(anchorEl);
  const phoneNumberMessageOptionsMenuOpen = Boolean(phoneNumberMessageOptionsMenuAnchorEl);

  const { whatsApps } = useWhatsApps();
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");

  const [shouldDisplayScrollButton, setShouldDisplayScrollButton] = useState(false);
  const [areThereNewMessagesScroll, setAreThereNewMessagesScroll] = useState(false);

  const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  ***************
  //  ** Callbacks **
  //  ***************
  const fetchMessagesCallback = useCallback(() => {
    const currentMessagesRequestId = ++messagesRequestIdRef.current;
    if (messagesAbortControllerRef.current) messagesAbortControllerRef.current.abort();

    const controller = new AbortController();
    messagesAbortControllerRef.current = controller;

    setLoading(true);
    setAreMessagesBeingFetched(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchMergeMessagesLocalStorage = () => {
        const mergeMessagesLocalStorage = localStorage.getItem("mergeMessagesFromDifferentConnections");
        if (!mergeMessagesLocalStorage) return false;

        const mergeMessagesArray = JSON.parse(mergeMessagesLocalStorage);
        if (mergeMessagesArray.length === 0) return false;

        return mergeMessagesArray.some(element => element.ticketId === parseInt(ticketId, 10) && element.userId === userId);
      };

      const fetchMessages = async () => {
        try {
          const shouldLoadMessages = (currentTicketId.current === ticketId && currentWhatsappId.current === whatsappId)
            || [1, 2, 3, 4].includes(ticketType);

          if (!shouldLoadMessages) {
            setAreMessagesBeingFetched(false);
            return;
          }

          const shouldMergeMessagesLocalStorage = ticketStatus === "group" ? false : fetchMergeMessagesLocalStorage();

          const { data } = await api.get(`/messages/${ticketId}`, {
            params: { pageNumber, shouldMergeMessagesLocalStorage }, signal: controller.signal
          });

          if (currentMessagesRequestId !== messagesRequestIdRef.current) return;
          
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          setFilesExistenceDictionary({ ...filesExistenceDictionary, ...data.filesExistenceDictionary });
          setHasMore(data.hasMore);
          setAreMessagesBeingFetched(false);

          const shouldScrollToBottom = pageNumber === 1 && data.messages.length > 1;
          if (shouldScrollToBottom) scrollToBottom(false);
        }
        catch (exception) {
          if (exception.code !== "ERR_CANCELED") {
            console.log("Messages List Use Effect 2 Exception:", exception);
            toastError(exception);
          }
        }
        finally {
          setLoading(false);
          setAreMessagesBeingFetched(false);
          isFetchingMessagesRef.current = false;
        }
      };

      fetchMessages();
    }, 500);

    return () => clearTimeout(delayDebounceFn);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageNumber, ticketId, whatsappId, ticketType]);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    setLastMessage(null); // reseting 'lastMessage' when the user changes ticket view from ticket A to ticket B
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    if (userWhatsapp?.id) setSelectedWhatsappId(userWhatsapp.id);
    else if (whatsApps.length === 1) setSelectedWhatsappId(whatsApps[0].id);
  }, [userWhatsapp, whatsApps]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setFilesExistenceDictionary({});

    currentTicketId.current = ticketId;
    if (whatsappId) currentWhatsappId.current = whatsappId;
  }, [dispatch, ticketId, whatsappId, ticketType]);

  useEffect(() => {
    const shouldFetchMessages = (whatsappId && ticketType === 0) || [1, 2, 3, 4].includes(ticketType);
    if (!shouldFetchMessages) return;

    fetchMessagesCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageNumber, ticketId, whatsappId, ticketType]);

  useEffect(() => {
    if (!shouldRefetchMessagesList) return;

    dispatch({ type: "RESET" });
    setPageNumber(1);
    setFilesExistenceDictionary({});

    fetchMessagesCallback();
    setShouldRefetchMessagesList(false);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetchMessagesList, setShouldRefetchMessagesList]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const checkForwardMessageCheckbox = async () => {
        document.getElementById(`checkbox-${selectedMessage.id}`).click();
      };

      if (isForwarding) {
        checkForwardMessageCheckbox();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [selectedMessage, isForwarding]);

  useEffect(() => {
    const handleFullScreen = () => setIsFullscreen(!!document.fullscreenElement);

    document.addEventListener("fullscreenchange", handleFullScreen);
    return () => document.removeEventListener("fullscreenchange", handleFullScreen);
  }, []);

  useEffect(() => {
    messagesList.forEach(message => {
      if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && videoRefs.current[message.id]) {
        const element = videoRefs.current[message.id];
        if (element && element.readyState === 0) element.load();
      }
    });
  }, [messagesList]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleMessageDoubleClick = (event, message) => {
    const notTicketPendingAndUserNotAdmin = !(ticketStatus === "pending" && userProfile === "user");

    const allowReply = ((ticketType === 0 && message.ticket) || [1, 3, 4].includes(ticketType))
    && (!message.fromMe || (message.fromMe && ![0, 9].includes(message.ack)))
    && notTicketPendingAndUserNotAdmin
    && !message.isInternal
    && ![
      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
    ].includes(message.mediaType);

    if (!allowReply) return;
    
    setReplyingMessage(message);
  };

  const toogleReactionOptionsContainer = (message) => {
    const reactionOptions = document.getElementById(`reactionOptions-${message.id}`);
    if (reactionOptions.style.display === "flex") { reactionOptions.style.display = "none"; }
    else { reactionOptions.style.display = "flex"; }
  };

  const handleShareMessage = (mediaUrl) => {
    if (!navigator.canShare) {
      toast.info(i18n.t("shareMessage.notSupport"));
    }
    else {
      fetch(mediaUrl)
        .then(res => res.blob())
        .then(async blob => {
          const file = new File([blob], `fileToShare.${blob.type.split("/")[1]}`, { type: blob.type });

          const shareData = {
            title: "📱 BestZap",
            text: "",
            url: "https://bestzap.fxsistemas.com.br/",
            files: [file],
          };

          if (navigator.canShare(shareData)) await navigator.share(shareData);
          else toast.error(i18n.t("shareMessage.errorSharing"));
      });
    }
  };

  const scrollToBottom = (smoothBehavior) => {
    if (smoothBehavior && lastMessageRef.current) lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
    else if (!smoothBehavior && lastMessageRef.current) lastMessageRef.current.scrollIntoView({});
  };

  const handleScrollToBottomButton = (smoothBehavior) => {
    setAreThereNewMessagesScroll(false);
    scrollToBottom(smoothBehavior);
  };

  const handleScroll = (event) => {
    const { scrollTop, offsetHeight, scrollHeight } = event.currentTarget;

    if (Math.ceil(scrollTop + offsetHeight) >= Math.ceil(scrollHeight)) {
      setShouldDisplayScrollButton(false);
      setAreThereNewMessagesScroll(false);
    }
    else {
      setShouldDisplayScrollButton(true);
    }

    if (!hasMore) return;

    if (scrollTop === 0) {
      event.currentTarget.scrollTop = 1;
      return;
    }

    if (isFetchingMessagesRef.current || loading) return;

    if (scrollTop < 100) {
      isFetchingMessagesRef.current = true;
      setPageNumber(previous => previous + 1);
    }
  };

  const handleOpenPrivateChatConversationModal = (selectedContact) => {
    setSelectedContactPrivateChat(selectedContact);
    setOpenPrivateChatConversationModal(true);
  };

  const handleClosePrivateChatConversationModal = () => {
    setSelectedContactPrivateChat(null);
    setOpenPrivateChatConversationModal(false);
    setSelectedWhatsappId("");
  };

  const handleOpenPrivateChat = async (selectedContactId) => {
    if (!selectedContactId) return;

    setLoading(true);

    if (!selectedWhatsappId) {
      toast.info(i18n.t("newTicketModal.toasts.whatsappNotSelected"));
    }
    else {
      try {
        const { data: ticket } = await api.post("/tickets", {
          contactId: selectedContactId,
          userId: userId,
          status: "open",
          isCreatedByUser: true,
          whatsappId: selectedWhatsappId
        });

        DefaultFunctionsUtils.redirectToTicketsPage(history, `/${ticket.id}`);
        setShouldSyncRouteOnMenu(true);
      }
      catch (exception) {
        console.log("Handle Open Private Chat Exception:", exception);
        toastError(exception);
      }
    }

    setLoading(false);
  };

  const selectForwardMessage = (messageId) => {
    const checkbox = document.getElementById(`checkbox-${messageId}`);

    if (checkbox.checked) {
      if (selectedForwardMessages.length < 20) {
        updateSelectedForwardMessages([...selectedForwardMessages, messageId]);
      }
      else {
        checkbox.checked = false;
        toast.info(i18n.t("messagesList.toast.forwardMessage.limit"));
      }
    }
    else {
      updateSelectedForwardMessages(selectedForwardMessages.filter(message => message !== messageId));
    }
  }

  const handleOpenMessageOptionsMenu = (event, message) => {
    if (!ticketStatus) return;
    if (ticketType === undefined || ticketType === null) return;
    if (ticketStatus === "pending" && userProfile === "user" && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.chat) return;
    if (ticketStatus === "closed") return;
    if (message.isDeleted) return;
    if (message.contact && [DefaultValuesUtils.SPECIAL_NAMES.whatsapp, DefaultValuesUtils.SPECIAL_NAMES.metaAi].includes(message.contact.name)) return;
    if (isContactBlocked || isContactInactive) return;
    
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenPhoneNumberMessageOptionsMenu = (event, phoneNumber, isMsgFromMe) => {
    const targetClassName = isMsgFromMe ? "messageRight" : "messageLeft";
    const parentNodeElement = getParentNodeByClassName(targetClassName, event.currentTarget);

    setPhoneNumberMessageOptionsMenuAnchorEl(parentNodeElement);
    setSelectedPhoneNumberMessage(phoneNumber);
  };

  const handleClosePhoneNumberMessageOptionsMenu = () => {
    setPhoneNumberMessageOptionsMenuAnchorEl(null);
  };

  const handleReaction = async (event, reaction, messageId, ticketId) => {
    document.getElementById(`reactionOptions-${messageId}`).style.display = "none";

    emojisplosion({
      physics: { gravity: 0.5, },
      emojis: [reaction],
      position: () => ({ x: event.clientX, y: event.clientY }),
    });
    
    await api.put(`/reaction/${messageId}`, { reaction, ticketId });
  };

  const handleOpenPollVotesModal = (message) => {
    setSelectedPollVotesMessage(message);
    setPollVotesModalOpen(true);
  };

  const handleClosePollVotesModal = () => { 
    setPollVotesModalOpen(false);
    setSelectedPollVotesMessage([]);
  };

  const handleOpenMessageEditModal = () => {
    setMessageEditModalOpen(true);
  };

  const handleCloseMessageEditModal = () => {
    setMessageEditModalOpen(false);
  };

  const handleOpenMessageEditHistoryModal = (message) => {
    setMessageEditHistoryModalOpen(true);
    setSelectedMessageEditHistory(message);
  };

  const handleCloseMessageEditHistoryModal = () => {
    setMessageEditHistoryModalOpen(false);
    setSelectedMessageEditHistory(null);
  };

  const handleOpenMessagePropertiesModal = (message) => {
    setMessagePropertiesModalOpen(true);
    setSelectedMessageProperties(message);
  };

  const handleCloseMessagePropertiesModal = () => {
    setMessagePropertiesModalOpen(false);
    setSelectedMessageProperties(null);
  };

  const checkMessageMedia = (message, isQuotedMessage=false) => {
    if (
      !isQuotedMessage
      && !filesExistenceDictionary[message.mediaUrl]
      && [
        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
      ].includes(message.mediaType)
    ) {
      return <MessageNotAvailable />;
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce) {
      return (<ViewOnceMessageWeb message={message} />)
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location && message.body.split('|').length >= 2) {
      const { imageLocation, linkLocation, descriptionLocation } = MessageUtils.extractLocationInfo(message);
      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button) { 
      const { buttonTitle, buttonFooter, buttonBody, buttonOptionsArray } = MessageUtils.extractButtonInfo(message);

      return(
        <>
          <b>{buttonTitle}</b>
          <p>{buttonBody}</p>
          <i className={classes.buttonListFooter}>{buttonFooter}</i>
          <hr />
          <center>
            {buttonOptionsArray.map((option, i) => (
              <Button
                color="inherit"
                variant="outlined"
                target="_blank"
                disabled
              >
                {option}
              </Button>
            ))}
          </center>
        </>
      );

    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list) { 
      const { listTitle, listBody, listOption, listFooter, listOptionsArray } = MessageUtils.extractListInfo(message);
      return (
        <>
          <b>{listTitle}</b>
          <p>{listBody}</p>
          <i className={classes.buttonListFooter}>{listFooter}</i>
          <center>
            <Button
              startIcon={<FormatListBulletedOutlined />}
              color="inherit"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
              disabled
            >
              {listOption}
            </Button>
          </center>
          
          <hr />

          <center>
            {listOptionsArray.map((option, i) => (
              <Button
                color="inherit"
                variant="outlined"
                target="_blank"
                href={message.mediaUrl}
                disabled
              >
                {option.split("%%")[0]}
              </Button>
            ))}
          </center>
        </>
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard) {
      const { contact, number } = MessageUtils.extractVcardInfo(message);
      return <VcardPreview contact={contact} number={number} enableHandleNewChat={true} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard) {
      const { contactsList } = MessageUtils.extractMultivcardInfo(message);
      return <MultiVcardPreview contacts={contactsList} enableStartChat={true} />;
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image && isQuotedMessage && !message.isViewOnce) {
      return <MessageImage message={message} ticketId={ticketId} replyMessage={true} />;
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image && !isQuotedMessage && !message.isViewOnce) {
      return <MessageImage message={message} ticketId={ticketId} replyMessage={false} />;
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image && isQuotedMessage && message.isViewOnce) {
      return <ViewOnceRepliedMessage isPhoto={true} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image && !isQuotedMessage && message.isViewOnce) {
      return <ViewOnceMessage message={message} isPhoto={true} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker) {
      return (
        <img
          className={clsx(classes.stickerMessage, {[classes.deletedStickerMessage]: message.isDeleted,})}
          src={message.mediaUrl} alt={message.body} width={225} height={225}
        />
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio) {
      return (
        <Audio
          currentAudioRef={currentAudioRef}
          transcript={message.transcript}
          url={message.mediaUrl}
          setAudioPlayer={setAudioPlayer}
          setCurrentPlayingAudioTime={setCurrentPlayingAudioTime}
          ticket={message.ticket}
          messageId={message.id}
          currentTicketId={ticketId}
          isDeleted={message.isDeleted}
          isVoice={message.isVoice}
          isFromMe={message.fromMe}
          isQuotedMessage={isQuotedMessage}
        />
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && !message.isViewOnce) {
      const refKey = message.id || message.mediaUrl;

      let mediaClass = isFullscreen ? `${classes.messageMediaFullScreen}` : `${classes.messageMedia}`;
      if (message.isDeleted) mediaClass += ` ${classes.deletedMediaMessage}`;

      return (
        <video
          key={`${refKey}-${message.updatedAt || ""}`} 
          ref={element => videoRefs.current[refKey] = element}
          className={mediaClass}
          src={message.mediaUrl}
          controls={!message.isGif}
          autoPlay={message.isGif}
          muted={message.isGif}
          loop={message.isGif}
        />
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && isQuotedMessage && message.isViewOnce) {
      return <ViewOnceRepliedMessage isPhoto={false} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && !isQuotedMessage && message.isViewOnce) {
      const refKey = message.id || message.mediaUrl;
      return <ViewOnceMessage message={message} isPhoto={false} refKey={refKey} videoRefs={videoRefs} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv) {
      return (
        <Videoplayer size={200} src={message.mediaUrl} boundaryColor={"#ff6961"} />
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation && !isQuotedMessage && !message.isRating) {
      return <PollPreview message={message} handleOpenPollVotesModal={handleOpenPollVotesModal} readonly={true} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation && !isQuotedMessage && message.isRating) {
      return <PollPreview message={message} handleOpenPollVotesModal={handleOpenPollVotesModal} readonly={true} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation && isQuotedMessage) {
      return <PollPreviewQuotedMessage message={message} />
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event && !isQuotedMessage) {
      return <EventPreview message={message} />;
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event && isQuotedMessage) {
      return <EventPreviewQuotedMessage message={message} />;
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite) {
      return <GroupInvitePreview message={message} isQuotedMessage={isQuotedMessage} />
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix) {
      return <PixMessage message={message} isQuoted={isQuotedMessage} />
    }
    
    else {
      const mediaExtension = message.mediaUrl.split(".")[message.mediaUrl.split(".").length - 1];

      const handleClick = (event) => {
        event.preventDefault();
        saveFile(message);
      }

      return (
        <>
          <div className={message.isDeleted ? `${classes.downloadMedia} ${classes.deletedMediaMessage}` : classes.downloadMedia}>
            {/* 
              *************
              ** Preview **
              *************
            */}
            {!isSmallScreen && !isQuotedMessage && mediaExtension === "pdf" && (
              <embed src={message.mediaUrl} width="500" height="375" type="application/pdf" />
            )}

            {!isSmallScreen && !isQuotedMessage && mediaExtension === "txt" && (
              <embed src={message.mediaUrl} width="500" height="375" type="text/plain"/>
            )}

            {!isSmallScreen && !isQuotedMessage && mediaExtension === "md" && (
              <embed src={message.mediaUrl} width="500" height="375" type="text/markdown"/>
            )}
            


            {/* 
              *************
              ** Buttons **
              *************
            */}
            <div className={classes.buttonsContainer}>
              {["md", "pdf", "txt"].includes(mediaExtension) && (
                <Button
                  startIcon={<VisibilityOutlined />}
                  color="inherit"
                  variant="outlined"
                  target="_blank"
                  href={message.mediaUrl}
                  className={classes.floatingButton}
                >
                  {i18n.t("messagesInput.view")}
                </Button>
              )}

              <Button
                startIcon={<GetApp />}
                color="inherit"
                variant="outlined"
                target="_blank"
                href="#"
                onClick={handleClick}
                className={classes.floatingButton}
              >
                {i18n.t("messagesInput.download")}
              </Button>
            </div>
            

            
            {/* 
              *************
              ** Caption **
              *************
            */}
            {isQuotedMessage && message.body}
          </div>
          <Divider />
        </>
      );
    }
  };

  const checkInteractiveMessage = (message) => {
    if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation) {
      return (
        <ListPreview message={message} ticketId={ticketId} />
      );
    } else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation) {
      return (
        <ButtonPreview message={message} ticketId={ticketId} />
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1 || message.ack === 2) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 4 || message.ack === 5) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderEndTicket = (message) => {
    if (!message.isLastMessage || message.ticket.type !== 0) return;

    const ticketEndingDate = message.ticket.isClosedAt
      ? extractDayMonthYearFromStringDateTime(message.ticket.isClosedAt)
      : "";

    let endTicketInfo = `${i18n.t("messagesListLabels.ticketEnd")} #${message.ticketId}`;

    if (message.ticket.user?.name) {
      endTicketInfo = `${endTicketInfo} (${message.ticket.user.name}) (${message.ticket.whatsapp?.name}) ${ticketEndingDate}`;
    }
    else if (message.ticket.directClosedByUser?.name) {
      endTicketInfo = `${endTicketInfo} (${message.ticket.directClosedByUser.name}) (${message.ticket.whatsapp?.name}) ${ticketEndingDate}`;
    }
    else {
      endTicketInfo = `${endTicketInfo} (-) (${message.ticket.whatsapp?.name}) ${ticketEndingDate}`;
    }

    return (
      <>
        <br />

        <div className={classes.lastMessageTicketContainer}>
          <hr className={classes.lastMessageTicketDivider} />
          <span className={classes.lastMessageTicketSpan}>{endTicketInfo}</span>
          <hr className={classes.lastMessageTicketDivider} />
        </div>

        <br />
      </>
    );
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }

    if (index <= messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
  };

  const renderDivToAutomaticScroll = (message, index) => {
    if (index === messagesList.length - 1) {
      return (<div key={`ref-${message.createdAt}`} ref={lastMessageRef} style={{ float: "left", clear: "both" }} />);
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;
      
      if (messageUser !== previousMessageUser) return (<span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>);
    }
  };

  const validateCheckMessageMediaForQuotedMessage = (message) => {
    if (!message.quotedMsg) return <></>;

    const shouldCheckMessageMedia = message.quotedMsg.mediaUrl
      || [
        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
      ].includes(message.quotedMsg.mediaType);

    if (shouldCheckMessageMedia) return checkMessageMedia(message.quotedMsg, true);
    else return <></>;
  };

  const renderQuotedMessage = (message) => {
    if (userProfile === "user" && message.quotedMsg?.isRating) return (<></>);

    return (
      <a className={classes.quotedMessageAnchor} href={`#messageContainer-${message.quotedMsg.id}`}>
        <div
          className={ticketType === 0
            ? clsx(classes.quotedContainerLeft, { [classes.quotedContainerRight]: message.fromMe })
            : clsx(classes.quotedContainerLeft, { [classes.quotedContainerRight]: ticketType === 3 && message.userId === userId })
          }
        >
          <span
            className={ticketType === 0
              ? clsx(classes.quotedSideColorLeft, { [classes.quotedSideColorRight]: message.quotedMsg?.fromMe })
              : clsx(classes.quotedSideColorLeft, { [classes.quotedSideColorRight]: ticketType === 3 && message.quotedMsg?.userId === userId })
            }
          />

          <div className={classes.quotedMsg}>
            {!message.quotedMsg?.fromMe && (
              <span className={classes.messageContactName}>{message.quotedMsg?.contact?.name}</span>
            )}

            {message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list 
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button 
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard 
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio 
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video 
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location 
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application 
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
              && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
              && (
                <MessageBodyWrapper
                  messageBody={message.quotedMsg?.body}
                  isMessageFromMe={message.fromMe}
                  handleOpenPhoneNumberMessageOptionsMenu={handleOpenPhoneNumberMessageOptionsMenu}
                />
              )
            }

            {message.quotedMsg?.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template && (
              TemplateMessageUtils.renderTemplate(message.quotedMsg, null, false)
            )}

            {message.quotedMsg?.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button && (
              <>
                <b>{message.quotedMsg?.body.split("##")[1]}</b>
                <p>{message.quotedMsg?.body.split("||")[0]}</p>
                <i className={classes.buttonListFooter}>{message.quotedMsg?.body.split("##")[2]}</i>
              </>
            )}

            {message.quotedMsg?.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list && (
              <>
                <b>{message.quotedMsg?.body.split("||")[0].split("%%")[0]}</b>
                <p>{message.quotedMsg?.body.split("||")[0].split("%%")[1]}</p>
                <i className={classes.buttonListFooter}>{message.quotedMsg?.body.split("||")[0].split("%%")[3]}</i>
              </>
            )}
            
            {message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template && (
              validateCheckMessageMediaForQuotedMessage(message)
            )}
          </div>
        </div>
      </a>
    );
  };

  const handleContextMenu = (event, message) => {
    event.preventDefault();

    const isFileNOtAvailable = !filesExistenceDictionary[message.mediaUrl] && [
      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
    ].includes(message.mediaType);
    
    if (isFileNOtAvailable) return;    
    if (message.isBroadcast) return;

    handleOpenMessageOptionsMenu(event, message);
  };

  const renderNotification = (message, ticketType) => {
    if (loading) return (<></>);
    if (!message && ticketType !== 0) return (<></>);

    const key = !!(message) ? message.id : CryptoUtils.generateUuid();
    const messageBody = !!(message) ? message.body : i18n.t("messagesInput.notifications.checkOldMessagesOnPhone");
    const isMessageFromMe = !!(message) ? message.fromMe : false;

    return (
      <span className={classes.notificationSpan} key={key}>
        <div className={classes.dailyTimestampText}>
          <MessageBodyWrapper
            messageBody={messageBody}
            isMessageFromMe={isMessageFromMe}
            handleOpenPhoneNumberMessageOptionsMenu={handleOpenPhoneNumberMessageOptionsMenu}
            shouldFormat={false}
          />
        </div>
      </span>
    );
  };

  const renderReactionOptions = (messageId, ticketId) => {
    return (
      <div id={`reactionOptions-${messageId}`} className={classes.reactionOptions}>
        <span className={`${classes.reactionOption} ${classes.reactionOptionOpacity}`} onClick={async (event) => await handleReaction(event, "", messageId, ticketId)}><NotInterestedOutlined /></span>
        {reactionOptions.map(reaction => (
          <span className={classes.reactionOption} onClick={async (event) => await handleReaction(event, reaction, messageId, ticketId)}>{reaction}</span>)
        )}
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length === 0) return (<></>);

    const lastMessageList = messagesList.filter(message => {
      return message.fromMe
        && [
          DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.chat
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
        ].includes(message.mediaType);
    });

    setLastMessage(lastMessageList[lastMessageList.length - 1]);

    return messagesList.map((message, index) => {
      const isInternalChatMessageNotFromCurrentUser = ticketType === 3 && message.userIdInternalChat === userId;
      const isInternalChatMessageFromCurrentUser = ticketType === 3 && message.userInternalChat !== userId;
      
      const isGroupInternalMessageNotFromCurrentUser = ticketType === 4 && message.userId !== userId;
      const isGroupInternalMessageFromCurrentUser = ticketType === 4 && message.userId === userId;

      const isMessageNotFromCurrentUser = !message.fromMe || isInternalChatMessageNotFromCurrentUser || isGroupInternalMessageNotFromCurrentUser;
      const isMessageFromCurrentUser = message.fromMe || isInternalChatMessageFromCurrentUser || isGroupInternalMessageFromCurrentUser;

      if (message.ack !== 9) {
        //  *****************
        //  ** Validations **
        //  *****************
        const isNotificationOrEphemeralSettingMessage = [
          DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.notification
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ephemeralSetting
        ].includes(message.mediaType);

        const isInternalMessage = message.isInternal
          || [
            DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
          ].includes(message.mediaType);

        // message is not shown when user is not admin and message is a rating poll
        const isRatingPoll = userProfile === "user" && message.isRating;

        const isGeneralMessage = ![
          DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template
        ].includes(message.mediaType);

        const isStickerMessage = message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker;

        const isTemplateMessage = message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template;

        //  *************
        //  ** Renders **
        //  *************
        if (isNotificationOrEphemeralSettingMessage) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderNotification(message, ticketType)}
            </React.Fragment>
          );
        }
        else if (isInternalMessage) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}

              <div id={`messageContainer-${message.id}`} className={`${classes.containerMessage} ${classes.containerMessageRight}`}>
                {/* 
                  *************
                  ** Message **
                  *************
                */}
                <div
                  className={`${classes.messageRight} ${classes.internalMessage}`}
                  onContextMenu={(e) => handleContextMenu(e, message)}
                >
                  {message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list
                  && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse 
                  && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button
                  && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse 
                  && !message.isBroadcast
                  && !isContactBlocked
                  && !isContactInactive
                  && (                    
                    <IconButton
                      variant="contained"
                      size="small"
                      id="messageActionsButton"
                      disabled={
                        message.isDeleted
                        || (
                          [
                            DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                          ].includes(message.mediaType)
                          && !filesExistenceDictionary[message.mediaUrl]
                        )
                      }
                      className={classes.messageActionsButton}
                      onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    ><ExpandMore /></IconButton>
                  )}

                  {(message.mediaUrl
                    || [
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
                    ].includes(message.mediaType)) && checkMessageMedia(message)}

                  {[
                    DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                    , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                    , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                    , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                    , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                    , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                  ].includes(message.mediaType)
                    && !message.mediaUrl
                    && (<MediaWithNoUrl />)}

                  <div
                    className={clsx(classes.textContentItem, {[classes.textContentItemDeleted]: message.isDeleted })}
                    style={message.isEdited ? { padding: "3px 80px 1em 6px" } : { padding: "3px 80px 6px 6px" }}
                  >
                    {![
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
                    ].includes(message.mediaType)
                    && message.body !== ""
                    && (
                      <MessageBodyWrapper
                        messageBody={message.body}
                        isMessageFromMe={message.fromMe}
                        handleOpenPhoneNumberMessageOptionsMenu={handleOpenPhoneNumberMessageOptionsMenu}
                      />
                    )}

                    {(
                      !message.isGif
                      || [
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType)
                    ) && (
                      <br />
                    )}

                    <span className={classes.timestamp}>
                      {message.isEdited && (
                        <span 
                          className={classes.editedMessage}
                          onClick={() => handleOpenMessageEditHistoryModal(message)}
                        >
                          {i18n.t("messagesInput.editedMessage") + " "}
                        </span>
                      )}

                      {format(parseISO(message.createdAt), "HH:mm")}
                      {renderMessageAck(message)}
                    </span>
                  </div>
                </div>

                {/* 
                  **********************
                  ** User Profile Pic **
                  **********************
                */}
                {message.user && message.user.name
                  && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.user.name} placement="top-start" arrow>
                      <Avatar alt={message.user.name} src={message.user.name} className={classes.contactAvatar} />
                    </Tooltip>
                  </div>
                )}
              </div>
              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else if (isRatingPoll) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}
              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else if (isGeneralMessage && isMessageNotFromCurrentUser) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}
              
              <div id={`messageContainer-${message.id}`} className={`${classes.containerMessage} ${classes.containerMessageLeft}`} onDoubleClick={(event) => handleMessageDoubleClick(event, message)}>

                {/* 
                  ******************************
                  ** Forward Message Checkbox **
                  ******************************
                */}
                {isForwarding 
                && !message.isDeleted
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call 
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse
                && !message.isViewOnce
                && (
                  <div className={classes.forwardMessageCheckbox}>
                    <Checkbox 
                      id={`checkbox-${message.id}`}
                      onClick={() => selectForwardMessage(message.id) }
                    />
                  </div>
                )}
                


                {/* 
                  ******************************
                  ** Group Member Profile Pic **                  
                  ******************************
                */}
                {ticketStatus === "group" && ticketType !== 4 && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.contact?.name} placement="top-start" arrow>
                      <Avatar
                        alt={message.contact?.name}
                        src={message.contact?.profilePicUrl}
                        className={classes.contactAvatar}
                        onClick={() => handleOpenPrivateChatConversationModal(message.contact)}
                      />
                    </Tooltip>
                  </div>
                )}

                {ticketStatus === "group" && ticketType === 4 && message?.user?.name && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.user.name} placement="top-start" arrow>
                      <Avatar alt={message.user.name} src={message.user.name} className={classes.contactAvatar} />
                    </Tooltip>
                  </div>
                )}



                {/* 
                  *************
                  ** Message **
                  *************
                */}
                <div className={classes.messageLeft} onContextMenu={(event) => handleContextMenu(event, message)} onDoubleClick={(event) => event.stopPropagation()}>
                  {!isContactBlocked && !isContactInactive && (
                      <IconButton
                        variant="contained"
                        size="small"
                        id="messageActionsButton"
                        disabled={
                          message?.isDeleted
                          || (
                            [
                              DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                            ].includes(message.mediaType)
                            && !filesExistenceDictionary[message.mediaUrl]
                          )
                        }
                        className={classes.messageActionsButton}
                        onClick={(event) => handleOpenMessageOptionsMenu(event, message)}
                      ><ExpandMore /></IconButton>
                  )}
                  
                  {isGroup && ticketType !== 4 && (
                    <span className={classes.messageContactName}>
                      {message.contact?.name}
                    </span>
                  )}

                  {message.quotedMsg && (
                    <div 
                      className={classes.textContentItem}
                      style={message.isEdited ? {padding: "3px 80px 1em 6px"} : {padding: "3px 80px 6px 6px"}}
                    >
                      {renderQuotedMessage(message)}
                    </div>
                  )}

                  {(message.mediaUrl
                    || [
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
                    ].includes(message.mediaType)) && checkMessageMedia(message)}

                  {!message.mediaUrl
                   && [
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                    ].includes(message.mediaType)
                    && (
                      <MediaWithNoUrl />
                    )
                  }
                  
                  
                  {/* 
                    ***************
                    ** TimeStamp **
                    ***************
                  */}
                  <div 
                    className={clsx(classes.textContentItem, {[classes.textContentItemDeleted]: message.isDeleted })}
                    style={message.isEdited ? { padding: "3px 80px 1em 6px" } : { padding: "3px 80px 6px 6px" }}
                  >
                    {message.isForwarded && (
                      <>
                        <i className={classes.forwardedMessage}>
                          <ReplyOutlined className={classes.horizontalFlip} />{i18n.t("messagesInput.forwardedMessage")}
                        </i>
                        <br />
                      </>
                    )}

                    {message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
                    && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation 
                    && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                    && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                    && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                    && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
                    && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
                    && message.body !== ""
                    && (
                      <MessageBodyWrapper
                        messageBody={message.body}
                        isMessageFromMe={message.fromMe}
                        handleOpenPhoneNumberMessageOptionsMenu={handleOpenPhoneNumberMessageOptionsMenu}
                      />
                    )}

                    {(message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                      || message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                      || message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      || !message.isGif)
                      && (<br />)}

                    {(message.adsSourceUrl || message.adsTitle || message.adsThumbnailUrl)
                      && <AdsMessage message={message} redirectFlag={true} />}

                    {message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call && message.isVideoCall && (
                      <div className={classes.callWrapper}>
                        <MissedVideoCallRounded className={classes.missedCall} /> 
                        <MessageBodyWrapper
                          messageBody={message.body}
                          isMessageFromMe={message.fromMe}
                          handleOpenPhoneNumberMessageOptionsMenu={handleOpenPhoneNumberMessageOptionsMenu}
                        />
                      </div>
                    )}

                    {message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call && !message.isVideoCall && (
                      <div className={classes.callWrapper}>
                        <PhoneMissedRounded className={classes.missedCall} />
                        <MessageBodyWrapper
                          messageBody={message.body}
                          isMessageFromMe={message.fromMe}
                          handleOpenPhoneNumberMessageOptionsMenu={handleOpenPhoneNumberMessageOptionsMenu}
                        />
                      </div>
                    )}

                    <span className={classes.timestamp}>
                      {message.isEdited && (
                        <span 
                          className={classes.editedMessage}
                          onClick={() => handleOpenMessageEditHistoryModal(message)}
                        >
                          {i18n.t("messagesInput.editedMessage") + " "}
                        </span>
                      )}

                      {message.isDeleted && (
                        <Block fontSize="small" className={classes.deletedIcon} />
                      )}

                      {message.isStarred && (
                        <StarOutlined className={classes.timeStampStar} />
                      )}

                      {message.isBroadcast && (
                        <RecordVoiceOverRounded className={classes.timeStampStar} />
                      )}

                      {message.isStatusReply && (
                        <FilterTiltShiftOutlined className={classes.timeStampStar} />
                      )}

                      {format(parseISO(message.createdAt), "HH:mm")}
                    </span>
                  </div>
                  

                  {/* 
                    ***************
                    ** Reactions **
                    ***************
                  */}
                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionList}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.contactReaction}
                          </span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.fromMeReaction}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {/*
                    ************************
                    ** Reaction Container **
                    ************************
                  */}
                  {renderReactionOptions(message.id, ticketId)}
                </div>
                
                {(
                  (message?.contactId !== null && !isContactBlocked && !isContactInactive)
                  || ticketType === 3
                )
                  && !message.isDeleted
                  && (
                    (
                      filesExistenceDictionary[message.mediaUrl]
                      && [
                        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType)
                    )
                    || (
                      ![
                        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType))
                  )
                  && (
                    <div className={classes.infoMessageContainer}>
                      {/* 
                        *********************
                        ** Reaction Button **
                        *********************
                      */}
                      <span id="reactionButton" className={classes.reactionButton} onClick={() => toogleReactionOptionsContainer(message)}>
                        <SentimentSatisfiedAltOutlined />
                      </span>



                      {/* 
                        ******************
                        ** Share Button **
                        ******************
                      */}
                      {(message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        || (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && !message.isGif))
                        && !message.isViewOnce
                        && (
                        <span
                          id="shareButton"
                          className={classes.reactionButton} 
                          onClick={() => handleShareMessage(message.mediaUrl)}
                        >
                          <ShareOutlined />
                        </span>
                      )}
                    </div>
                )}
              </div>

              {/* 
                **************************
                ** Interactive Messages **
                **************************
              */}
              {(message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation || message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation) && (
                checkInteractiveMessage(message)
              )}
              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else if (isStickerMessage && isMessageNotFromCurrentUser) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}
              
              <div id={`messageContainer-${message.id}`} className={`${classes.containerMessage} ${classes.containerMessageLeft}`} onDoubleClick={(event) => handleMessageDoubleClick(event, message)}>


                {/* 
                  ******************************
                  ** Forward Message Checkbox **
                  ******************************
                */}
                {isForwarding
                && !message.isDeleted
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                && !message.isViewOnce
                && (
                  <div className={classes.forwardMessageCheckbox}>
                    <Checkbox 
                      onClick={() => selectForwardMessage(message.id) }
                      id={`checkbox-${message.id}`}
                    />
                  </div>
                )}



                {/* 
                  ******************************
                  ** Group Member Profile Pic **                  
                  ******************************
                */}
                {ticketStatus === "group" && ticketType !== 4 && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.contact?.name} placement="top-start" arrow>
                      <Avatar
                        alt={message.contact?.name}
                        src={message.contact?.profilePicUrl}
                        className={classes.contactAvatar}
                        onClick={() => handleOpenPrivateChatConversationModal(message.contact)}
                      />
                    </Tooltip>
                  </div>
                )}

                {ticketStatus === "group" && ticketType === 4 && message?.user?.name && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.user.name} placement="top-start" arrow>
                      <Avatar alt={message.user.name} src={message.user.name} className={classes.contactAvatar} />
                    </Tooltip>
                  </div>
                )}



                {/* 
                  *************
                  ** Message **
                  *************
                */}
                <div className={classes.messageLeftSticker} onContextMenu={(e) => handleContextMenu(e, message)}>
                  {!isContactBlocked && !isContactInactive && (
                    <IconButton
                      variant="contained"
                      size="small"
                      id="messageActionsButton"
                      disabled={
                        message?.isDeleted || (
                          !filesExistenceDictionary[message.mediaUrl]
                          && [
                            DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                          ].includes(message.mediaType)
                        )
                      }
                      className={classes.messageActionsButton}
                      onClick={(event) => handleOpenMessageOptionsMenu(event, message)}
                    ><ExpandMore /></IconButton>
                  )}

                  {message.isForwarded && (
                    <>
                      <i className={classes.forwardedMessage}>
                        <ReplyOutlined className={classes.horizontalFlip} />{i18n.t("messagesInput.forwardedMessage")}
                      </i>
                      <br />
                    </>
                  )}

                  {message.quotedMsg && (
                    <div
                      className={classes.textContentItem}
                      style={message.isEdited ? {padding: "3px 80px 1em 6px"} : {padding: "3px 80px 6px 6px"}}
                    >
                      {renderQuotedMessage(message)}
                    </div>
                  )}

                  {!filesExistenceDictionary[message.mediaUrl]
                    ? (
                      <>
                        <MessageNotAvailable />
                        <br />
                      </>
                    )
                    : (
                      <img
                        className={clsx(classes.stickerMessage, {[classes.deletedStickerMessage]: message.isDeleted,})}
                        src={message.mediaUrl} alt={message.body} width={225} height={225}
                      />
                    )
                  }
                  
                  <div 
                    className={clsx(classes.textContentItem, {[classes.textContentItemDeleted]: message.isDeleted,})}
                    style={message.isEdited ? { padding: "3px 80px 1em 6px" } : { padding: "3px 80px 6px 6px" }}
                  >
                    <span className={`${classes.timestamp} ${classes.timestampStickerLeft}`}>
                      {message.isDeleted && (
                        <Block fontSize="small" className={classes.deletedIcon} />
                      )}

                      {message.isStarred && (
                        <StarOutlined className={classes.timeStampStar} />
                      )}

                      {message.isBroadcast && (
                        <RecordVoiceOverRounded className={classes.timeStampStar} />
                      )}

                      {message.isStatusReply && (
                        <FilterTiltShiftOutlined className={classes.timeStampStar} />
                      )}

                      {format(parseISO(message.createdAt), "HH:mm")}
                    </span>
                  </div>
                  
                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionListSticker}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.contactReaction}</span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.fromMeReaction}</span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {/*
                    ************************
                    ** Reaction Container **
                    ************************
                  */}
                  {renderReactionOptions(message.id, ticketId)}
                </div>



                {/* 
                  *********************
                  ** Reaction Button **
                  *********************
                */}
                {
                  (
                  (message?.contactId !== null && !isContactBlocked && !isContactInactive)
                  || ticketType === 3
                  )
                  && !message.isDeleted
                  && (
                    (
                      filesExistenceDictionary[message.mediaUrl]
                      && [
                        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType)
                    )
                    || (
                      ![
                        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType)
                    )
                  )
                  && (
                  <span id="reactionButton" className={classes.reactionButton} onClick={() => toogleReactionOptionsContainer(message)}>
                    <SentimentSatisfiedAltOutlined />
                  </span>                
                )}
              </div>
              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else if (isTemplateMessage && isMessageNotFromCurrentUser) {
          const templateType = TemplateMessageUtils.getTemplateTypeByMessage(message);

          const isTemplateTypeValid = [
            DefaultValuesUtils.TEMPLATE_TYPES.hydratedFourRowTemplate
            , DefaultValuesUtils.TEMPLATE_TYPES.hydratedTemplate
          ].includes(templateType);

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}
              
              <div id={`messageContainer-${message.id}`} className={`${classes.containerMessage} ${classes.containerMessageLeft}`} onDoubleClick={(event) => handleMessageDoubleClick(event, message)}>
                {/* 
                  ******************************
                  ** Group Member Profile Pic **
                  ******************************
                */}
                {ticketStatus === "group" && ticketType !== 4 && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.contact?.name} placement="top-start" arrow>
                      <Avatar
                        alt={message.contact?.name}
                        src={message.contact?.profilePicUrl}
                        className={classes.contactAvatar}
                        onClick={() => handleOpenPrivateChatConversationModal(message.contact)}
                      />
                    </Tooltip>
                  </div>
                )}

                {/* 
                  *************
                  ** Message **
                  *************
                */}
                <div
                  className={classes.messageLeft}
                  onContextMenu={(event) => {
                    if (!isTemplateTypeValid) return;
                    handleContextMenu(event, message)
                  }}
                  onDoubleClick={(event) => event.stopPropagation()}
                >
                  {!isContactBlocked 
                  && !isContactInactive
                  && isTemplateTypeValid
                  && (
                    <IconButton
                      variant="contained"
                      size="small"
                      id="messageActionsButton"
                      className={classes.messageActionsButton}
                      disabled={message?.isDeleted}
                      onClick={(event) => handleOpenMessageOptionsMenu(event, message)}
                    ><ExpandMore /></IconButton>
                  )}

                  {isGroup && ticketType !== 4 && (
                    <span className={classes.messageContactName}>
                      {message.contact?.name}
                    </span>
                  )}

                  {message.quotedMsg && (
                    <div
                      className={classes.textContentItem}
                      style={message.isEdited ? {padding: "3px 80px 1em 6px"} : {padding: "3px 80px 6px 6px"}}
                    >
                      {renderQuotedMessage(message)}
                    </div>
                  )}

                  {/* 
                    ***************
                    ** TimeStamp **
                    ***************
                  */}
                  <div
                    className={clsx(classes.textContentItem, {[classes.textContentItemDeleted]: message.isDeleted})}
                    style={message.isEdited ? { padding: "3px 80px 1em 6px" } : { padding: "3px 80px 6px 6px" }}
                  >
                    {TemplateMessageUtils.renderTemplate(message, ticketId, true)}

                    <span className={classes.timestamp}>
                      {message.isDeleted && (
                        <Block fontSize="small" className={classes.deletedIcon} />
                      )}

                      {message.isStarred && (
                        <StarOutlined className={classes.timeStampStar} />
                      )}

                      {message.isBroadcast && (
                        <RecordVoiceOverRounded className={classes.timeStampStar} />
                      )}

                      {message.isStatusReply && (
                        <FilterTiltShiftOutlined className={classes.timeStampStar} />
                      )}

                      {format(parseISO(message.createdAt), "HH:mm")}
                    </span>
                  </div>

                  {/* 
                    ***************
                    ** Reactions **
                    ***************
                  */}
                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionList}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.contactReaction}
                          </span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.fromMeReaction}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {/* 
                    ************************
                    ** Reaction Container **
                    ************************
                  */}
                  {renderReactionOptions(message.id, ticketId)}
                </div>

                {((message?.contactId !== null && !isContactBlocked && !isContactInactive) || ticketType === 3)
                  && !message.isDeleted
                  && (
                    <div className={classes.infoMessageContainer}>
                      {/* 
                        *********************
                        ** Reaction Button **
                        *********************
                      */}
                      <span id="reactionButton" className={classes.reactionButton} onClick={() => toogleReactionOptionsContainer(message)}>
                        <SentimentSatisfiedAltOutlined />
                      </span>
                    </div>
                )}
              </div>

              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else if (isGeneralMessage && isMessageFromCurrentUser) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}

              <div id={`messageContainer-${message.id}`} className={`${classes.containerMessage} ${classes.containerMessageRight}`} onDoubleClick={(event) => handleMessageDoubleClick(event, message)}>

                {/* 
                ************************
                ** Info MSG Container **
                ************************
                */}
                {((message?.contactId !== null && !isContactBlocked && !isContactInactive)
                  || ([3, 4].includes(message.ticket.type)))
                  && !message.isDeleted
                  && (
                    (
                      filesExistenceDictionary[message.mediaUrl]
                      && [
                        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType)
                    )
                    || (
                      ![
                        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType)
                    )
                  )
                  && (
                  <div className={classes.infoMessageContainer}>
                    {/* 
                      ******************
                      ** Share Button **
                      ******************
                    */}
                    {(message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image 
                      || (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && !message.isGif))
                      && !message.isViewOnce
                      && (
                      <span
                        id="shareButton"
                        className={classes.reactionButton} 
                        onClick={() => handleShareMessage(message.mediaUrl)}
                      >
                        <ShareOutlined />
                      </span>
                    )}            
                  </div>
                )}




                {/* 
                  *************
                  ** Message **
                  *************
                */}
                <div className={classes.messageRight} onContextMenu={(event) => handleContextMenu(event, message)} onDoubleClick={(event) => event.stopPropagation()}>
                  {message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse 
                  && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse
                  && !message.isBroadcast
                  && !isContactBlocked
                  && !isContactInactive
                  && (                    
                    <IconButton
                      variant="contained"
                      size="small"
                      id="messageActionsButton"
                      disabled={
                        message?.isDeleted
                        || (
                          !filesExistenceDictionary[message.mediaUrl]
                          && [
                            DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                          ].includes(message.mediaType)
                        )
                      }
                      className={classes.messageActionsButton}
                      onClick={(event) => handleOpenMessageOptionsMenu(event, message)}
                    ><ExpandMore /></IconButton>
                  )}

                  
                  {message.quotedMsg && (
                    <div
                      className={classes.textContentItem}
                      style={message.isEdited ? {padding: "3px 80px 1em 6px"} : {padding: "3px 80px 6px 6px"}}
                    >
                      {renderQuotedMessage(message)}
                    </div>
                  )}

                  {(message.mediaUrl 
                    || [
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
                    ].includes(message.mediaType)) && checkMessageMedia(message)}

                  {!message.mediaUrl
                    && [
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                    ].includes(message.mediaType)
                    && (
                      <MediaWithNoUrl />
                    )
                  }

                  <div
                    className={clsx(classes.textContentItem, {[classes.textContentItemDeleted]: message.isDeleted })}
                    style={message.isEdited ? { padding: "3px 80px 1em 6px" } : { padding: "3px 80px 6px 6px" }}
                  >
                    {message.isForwarded && (
                      <>
                        <i className={classes.forwardedMessage}>
                          <ReplyOutlined className={classes.horizontalFlip} />{i18n.t("messagesInput.forwardedMessage")}
                        </i>
                        <br />
                      </>
                    )}

                    {message.isDeleted && (
                      <Block
                        color="disabled"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    )}

                    {(message.adsSourceUrl || message.adsTitle || message.adsThumbnailUrl)
                    && <AdsMessage message={message} redirectFlag={true} />}

                    {![
                      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
                      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
                    ].includes(message.mediaType)
                    && message.body !== ""
                    && (
                      <MessageBodyWrapper
                        messageBody={message.body}
                        isMessageFromMe={message.fromMe}
                        handleOpenPhoneNumberMessageOptionsMenu={handleOpenPhoneNumberMessageOptionsMenu}
                      />
                    )}

                    {(
                      message.isGif
                      || [
                        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                      ].includes(message.mediaType))
                      && (
                      <br />
                    )}

                    <span className={classes.timestamp}>
                      {message.isEdited && (
                        <span 
                          className={classes.editedMessage}
                          onClick={() => handleOpenMessageEditHistoryModal(message)}
                        >
                          {i18n.t("messagesInput.editedMessage") + " "}
                        </span>
                      )}

                      {message.isStarred && (
                        <StarOutlined className={classes.timeStampStar} />
                      )}

                      {message.isBroadcast && (
                        <RecordVoiceOverRounded className={classes.timeStampStar} />
                      )}

                      {message.isStatusReply && (
                        <FilterTiltShiftOutlined className={classes.timeStampStar} />
                      )}

                      {format(parseISO(message.createdAt), "HH:mm")}
                      {renderMessageAck(message)}
                    </span>
                  </div>

                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionList}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.contactReaction}
                          </span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.fromMeReaction}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
                
                

                {/* 
                  **********************
                  ** User Profile Pic **
                  **********************
                */}
                {message?.user?.name && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.user.name} placement="top-start" arrow>
                      <Avatar alt={message.user.name} src={message.user.name} className={classes.contactAvatar} />
                    </Tooltip>
                  </div>
                )}


                
                {/* 
                  ******************************
                  ** Forward Message Checkbox **
                  ******************************
                */}
                {isForwarding
                && !message.isDeleted
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse
                && message.ack !== 0 && message.ack !== 9
                && !message.isViewOnce
                && (
                  <div className={classes.forwardMessageCheckbox}>
                    <Checkbox 
                      onClick={() => selectForwardMessage(message.id) }
                      id={`checkbox-${message.id}`}
                    />
                  </div>
                )}
              </div>

              {/* 
                **************************
                ** Interactive Messages **
                **************************
              */}
              {(message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation || message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation) && (
                checkInteractiveMessage(message)
              )}

              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else if (isStickerMessage && isMessageFromCurrentUser) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}
              
              <div id={`messageContainer-${message.id}`} className={`${classes.containerMessage} ${classes.containerMessageRight}`} onDoubleClick={(event) => handleMessageDoubleClick(event, message)}>
                {/* 
                  *************
                  ** Message **
                  *************
                */}
                <div className={classes.messageRightSticker} onContextMenu={(e) => handleContextMenu(e, message)}>

                  {!message.isBroadcast && !isContactBlocked && !isContactInactive && (
                    <IconButton
                      variant="contained"
                      size="small"
                      id="messageActionsButton"
                      disabled={
                        message?.isDeleted
                        || (
                          !filesExistenceDictionary[message.mediaUrl]
                          && [
                            DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
                            , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
                          ].includes(message.mediaType)
                        )
                      }
                      className={classes.messageActionsButton}
                      onClick={(event) => handleOpenMessageOptionsMenu(event, message)}
                    ><ExpandMore /></IconButton>
                  )}

                  {message.isForwarded && (
                    <>
                      <i className={classes.forwardedMessage}>
                        <ReplyOutlined className={classes.horizontalFlip} />{i18n.t("messagesInput.forwardedMessage")}
                      </i>
                      <br />
                    </>
                  )}

                  {message.quotedMsg && (
                    <div
                      className={classes.textContentItem}
                      style={message.isEdited ? {padding: "3px 80px 1em 6px"} : {padding: "3px 80px 6px 6px"}}
                    >
                      {renderQuotedMessage(message)}
                    </div>
                  )}

                  {!filesExistenceDictionary[message.mediaUrl]
                    ? (
                      <>
                        <MessageNotAvailable />
                        <br />
                      </>
                    )
                    : (
                      <img
                        className={clsx(classes.stickerMessage, {[classes.deletedStickerMessage]: message.isDeleted,})}
                        src={message.mediaUrl} alt={message.body} width={225} height={225}
                      />
                    )
                  }

                  <div 
                    className={clsx(classes.textContentItem, {[classes.textContentItemDeleted]: message.isDeleted,})}
                    style={message.isEdited ? { padding: "3px 80px 1em 6px" } : { padding: "3px 80px 6px 6px" }}
                  >

                    <span className={`${classes.timestamp} ${classes.timestampStickerRight}`}>
                      {message.isDeleted && (
                        <Block fontSize="small" className={classes.deletedIcon} />
                      )}

                      {message.isStarred && (
                        <StarOutlined className={classes.timeStampStar} />
                      )}

                      {message.isBroadcast && (
                        <RecordVoiceOverRounded className={classes.timeStampStar} />
                      )}

                      {message.isStatusReply && (
                        <FilterTiltShiftOutlined className={classes.timeStampStar} />
                      )}

                      {format(parseISO(message.createdAt), "HH:mm")}
                      {renderMessageAck(message)}
                    </span>
                  </div>
                  
                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionListSticker}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.contactReaction}</span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.fromMeReaction}</span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>



                {/* 
                  **********************
                  ** User Profile Pic **
                  **********************
                */}
                {message?.user?.name && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.user.name} placement="top-start" arrow>
                      <Avatar alt={message.user.name} src={message.user.name} className={classes.contactAvatar} />
                    </Tooltip>
                  </div>
                )}



                {/* 
                  ******************************
                  ** Forward Message Checkbox **
                  ******************************
                */}
                {isForwarding
                && !message.isDeleted
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
                && message.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
                && message.ack !== 0 && message.ack !== 9
                && !message.isViewOnce
                && (
                  <div className={classes.forwardMessageCheckbox}>
                    <Checkbox 
                      onClick={() => selectForwardMessage(message.id) }
                      id={`checkbox-${message.id}`}
                    />
                  </div>
                )}
              </div>
              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else if (isTemplateMessage && isMessageFromCurrentUser) {
          const templateType = TemplateMessageUtils.getTemplateTypeByMessage(message);

          const isTemplateTypeValid = [
            DefaultValuesUtils.TEMPLATE_TYPES.hydratedFourRowTemplate
            , DefaultValuesUtils.TEMPLATE_TYPES.hydratedTemplate
          ].includes(templateType);

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderDivToAutomaticScroll(message, index)}
              {renderMessageDivider(message, index)}

              <div id={`messageContainer-${message.id}`} className={`${classes.containerMessage} ${classes.containerMessageRight}`} onDoubleClick={(event) => handleMessageDoubleClick(event, message)}>
                {/* 
                  *************
                  ** Message **
                  *************
                */}
                <div
                  className={classes.messageRight}
                  onContextMenu={(event) => {
                    if (!isTemplateTypeValid) return;
                    handleContextMenu(event, message)
                  }}
                  onDoubleClick={(event) => event.stopPropagation()}
                >
                  {!message.isBroadcast
                  && !isContactBlocked
                  && !isContactInactive
                  && isTemplateTypeValid
                  && (
                    <IconButton
                      variant="contained"
                      size="small"
                      id="messageActionsButton"
                      disabled={message?.isDeleted}
                      className={classes.messageActionsButton}
                      onClick={(event) => handleOpenMessageOptionsMenu(event, message)}
                    ><ExpandMore /></IconButton>
                  )}

                  {message.quotedMsg && (
                    <div
                      className={classes.textContentItem}
                      style={message.isEdited ? {padding: "3px 80px 1em 6px"} : {padding: "3px 80px 6px 6px"}}
                    >
                      {renderQuotedMessage(message)}
                    </div>
                  )}

                  <div
                    className={clsx(classes.textContentItem, {[classes.textContentItemDeleted]: message.isDeleted })}
                    style={message.isEdited ? { padding: "3px 80px 1em 6px" } : { padding: "3px 80px 6px 6px" }}
                  >
                    {message.isDeleted && (
                      <Block color="disabled" fontSize="small" className={classes.deletedIcon} />
                    )}

                    {TemplateMessageUtils.renderTemplate(message, ticketId, true)}

                    <span className={classes.timestamp}>
                      {message.isStarred && (
                        <StarOutlined className={classes.timeStampStar} />
                      )}

                      {message.isBroadcast && (
                        <RecordVoiceOverRounded className={classes.timeStampStar} />
                      )}

                      {message.isStatusReply && (
                        <FilterTiltShiftOutlined className={classes.timeStampStar} />
                      )}

                      {format(parseISO(message.createdAt), "HH:mm")}
                      {renderMessageAck(message)}
                    </span>
                  </div>

                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionList}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.contactReaction}
                          </span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>
                            {message.fromMeReaction}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>

                {/* 
                  **********************
                  ** User Profile Pic **
                  **********************
                */}
                {message?.user?.name && (
                  <div className={classes.contactAvatarContainer}>
                    <Tooltip title={message.user.name} placement="top-start" arrow>
                      <Avatar alt={message.user.name} src={message.user.name} className={classes.contactAvatar} />
                    </Tooltip>
                  </div>
                )}
              </div>

              {renderEndTicket(message)}
            </React.Fragment>
          );
        }
        else {
          return (<></>);
        }
      }
      else {
        return (<></>);
      }
    });
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.messagesListWrapper}>
      <SocketMessagesList
        dispatch={dispatch}
        setAreThereNewMessagesScroll={setAreThereNewMessagesScroll}
        shouldDisplayScrollButton={shouldDisplayScrollButton}
        scrollToBottom={scrollToBottom}
        filesExistenceDictionary={filesExistenceDictionary}
        setFilesExistenceDictionary={setFilesExistenceDictionary}
      />

      <MessageEditModal
        open={messageEditModalOpen}
        onClose={handleCloseMessageEditModal}
        message={selectedMessage}
      />

      <PollVotesModal
        open={pollVotesModalOpen}
        onClose={handleClosePollVotesModal}
        message={selectedPollVotesMessage}
      />

      <MessageEditHistoryModal
        open={messageEditHistoryModalOpen}
        onClose={handleCloseMessageEditHistoryModal}
        message={selectedMessageEditHistory}
      />

      <MessagePropertiesModal
        open={messagePropertiesModalOpen}
        onClose={handleCloseMessagePropertiesModal}
        message={selectedMessageProperties}
      />

      <MessageOptionsMenu
        message={selectedMessage}
        ticketType={ticketType}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        onUpdate={updateIsForwarding}
        updateIsForwarding={updateIsForwarding}
        isForwarding={isForwarding}
        handleOpenMessageEditModal={handleOpenMessageEditModal}
        handleOpenMessagePropertiesModal={handleOpenMessagePropertiesModal}
        ticketStatus={ticketStatus}
      />

      <PhoneNumberMessageOptionsMenu
        anchorEl={phoneNumberMessageOptionsMenuAnchorEl}
        handleClose={handleClosePhoneNumberMessageOptionsMenu}
        menuOpen={phoneNumberMessageOptionsMenuOpen}
        phoneNumber={selectedPhoneNumberMessage}
        setTabOpen={setTabOpen}
      />

      <ConfirmationModal
        title={selectedContactPrivateChat && `${i18n.t("messagesList.confirmationModal.openPrivateChat.title")} ${selectedContactPrivateChat.name}?`}
        open={openPrivateChatConversationModal}
        onClose={handleClosePrivateChatConversationModal}
        onConfirm={async () => await handleOpenPrivateChat(selectedContactPrivateChat.id)}
      >
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{i18n.t("newTicketModal.connection")}</InputLabel>
          <Select
            value={selectedWhatsappId}
            onChange={(e) => setSelectedWhatsappId(e.target.value)}
            label={i18n.t("newTicketModal.connection")}
            fullWidth
          >
            <MenuItem value={""}>&nbsp;</MenuItem>
            {whatsApps.map((whatsapp) => <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>)}
          </Select>
        </FormControl>
      </ConfirmationModal>

      
      <div id="messagesList" onDragStart={disableDragEvent} className={classes.messagesList} onScroll={handleScroll}>
        {renderNotification(null, ticketType)}
        {renderMessages()}
        
        {ticketType === 2 && botAnswerDelay && (
          <div className={classes.dotElasticContainer}>
            <div class="dot-elastic" />
          </div>
        )}

        <div
          style={{display: shouldDisplayScrollButton ? "block" : "none" }}
          className={classes.scrollBottomContainer}
          onClick={() => handleScrollToBottomButton(true)}
        >
          {areThereNewMessagesScroll && (<span>{i18n.t("messagesInput.scrollToSeeMessages")}</span>)}
          
          <IconButton className={classes.scrollBottomButton}>
            <ExpandMoreOutlined />
          </IconButton>
        </div>
      </div>

      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;